import KroModal from "features/components/modals/kroModal";
import { CampaignModelMapper } from "features/campaign/domain/models/campaignModel";
import { FC, useState, useEffect } from "react";
import KroTextField from "features/components/inputs/kroTextField";
import { KroButton } from "features/components/buttons/kroButton";
import { KroInputFieldType } from "features/campaign/domain/enums/kroInputFieldType.enum";
import { useForm } from "@tanstack/react-form";
import Validation from "common/utils/validation";
import { useCheckCustomLinkAvailability } from "../../hooks/useCheckCustomLinkAvailability";
import { VanityUrlCheckResponse } from "features/dashboard/domain/model/vanityUrlCheckResponse";
import { toast } from "react-toastify";
import { useCreateCustomCampaignLink } from "../../hooks/useCreateCustomCampaignLink";
import useFetchCampaign from "features/campaign/presentation/hooks/useFetchCampaign";
import { queryClient } from "index";

interface CustomizeCampaignLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaign: CampaignModelMapper | undefined;
  setIsSuccessModalOpen: (isOpen: boolean) => void;
  refetch: () => void;
}

const CustomizeCampaignLinkModal: FC<CustomizeCampaignLinkModalProps> = ({
  isOpen,
  onClose,
  campaign,
  setIsSuccessModalOpen,
  refetch,
}: CustomizeCampaignLinkModalProps) => {
  const [error, setError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);

  const { data: latestCampaign } = useFetchCampaign(campaign?.id ?? "");


  const { mutate: createCustomCampaignLink, isPending: isCreatingCustomCampaignLink } = useCreateCustomCampaignLink(
    (error: Error) => {
      toast.error(error.message);
    },
    (data: CampaignModelMapper) => {
      queryClient.invalidateQueries({ queryKey: ["fetchCampaign", campaign?.id] });
      refetch();
      toast.success("Custom link created successfully");
      onClose();
      setIsSuccessModalOpen(true);
    }
  );

  const mutation = useCheckCustomLinkAvailability(
    (error: Error) => {
      setError(error.message);
      setIsAvailable(false);
    },
    (data: VanityUrlCheckResponse) => {
      setError(null);
      setIsAvailable(data.available);
    }
  );

  const validateCustomLink = async (
    link: string | undefined,
  ): Promise<string | undefined> => {
    try {
      setIsValidating(true);
      await mutation.mutateAsync(link ?? "");

      return undefined;
    } catch (error) {
      if (error instanceof Error) {
        return error.message;
      }
    } finally {
      setIsValidating(false);
    }
  };


  const form = useForm<{
    link: string;
  }>({
    defaultValues: {
      link: latestCampaign?.vanityUrl ? `${latestCampaign.vanityUrl}` : "",
    },
    onSubmit: (values) => {
      if (isAvailable) {
        const customPart = values.value.link;
        createCustomCampaignLink({
          campaignId: campaign?.id ?? "",
          url: customPart
        });
      } else {
        setError("Please check if the link is available first");
      }
    }
  });


  useEffect(() => {
    setError(null);
    setIsAvailable(null);
    form.reset();


  }, [isOpen, campaign?.vanityUrl, form]);


  const isDisabled = !form.state.values.link || !!error || !isAvailable;

  return <KroModal className="p-0" isOpen={isOpen} showCloseButton={true} title="Create custom campaign link" onClose={onClose} maxWidth={608} maxHeight={295}>
    <div className="px-[40px] mt-10" onClick={(e) => {
      e.stopPropagation();
      e.preventDefault();
    }}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <form.Field
          name="link"
          validators={{
            onChangeAsyncDebounceMs: 1000,
            onChangeAsync: async ({ value }) => {
              if (!value) return;
              const customPart = value;
              const syncError = Validation.customLink(value?.toString());
              if (syncError) return syncError;
              return await validateCustomLink(customPart);
            },
          }}
        >
          {(field) => (
            <div>
              <KroTextField
                id={field.name}
                name={field.name}
                label="Link"
                value={field.state.value}
                onChange={(e) => {
                  if (isAvailable !== null) setIsAvailable(null);
                  if (error) setError(null);
                  const value = e.target.value.replace(/\s+/g, '');
                  field.handleChange(value);
                }}
                type={KroInputFieldType.TEXTWITHADDONS}
                isLoading={isValidating}
                leadingAddonText="giv.ng/"
                placeHolder="Enter your custom text"

              />
              {field.state.meta.errors.length > 0 && (
                <p className="text-[#FB3748] font-normal text-[12px] leading-[16px] tracking-[-0.5px] mt-2 emphasis-red">
                  {field.state.meta.errors.join(", ")}
                </p>
              )}
              {isAvailable === true && !error && field.state.meta.errors.length === 0 && (
                <p className="text-green-600 font-normal text-[12px] leading-[16px] tracking-[-0.5px] mt-2">
                  This link is available
                </p>
              )}
              {isAvailable === false && !error && field.state.meta.errors.length === 0 && (
                <p className="text-[#FB3748] font-normal text-[12px] leading-[16px] tracking-[-0.5px] mt-2">
                  URL already taken
                </p>
              )}
            </div>
          )}
        </form.Field>
        <KroButton
          className="w-full mt-10 mb-10"
          title="Create Custom Link"
          onclick={form.handleSubmit}
          isDisabled={isDisabled}
          isLoading={isCreatingCustomCampaignLink}
        />
      </form>
    </div>
  </KroModal>
};

export default CustomizeCampaignLinkModal;